// ABOUTME: Offers visitors the regional Takanaka store that can ship to them.
// ABOUTME: Only ever renders a link — it never redirects, by design.

import { Component } from '@theme/component';
import { resolveRegion } from './region-resolve.js';
import { shouldSuppress, recordDismissal, recordChoice } from './region-persistence.js';

const GEO_ENDPOINT = '/browsing_context_suggestions.json';
const GEO_TIMEOUT_MS = 1500;

// Storage globals are read lazily, behind try/catch: with cookies fully
// blocked, Chrome throws SecurityError on the property access itself, before
// any method call — a throw here would kill the module before
// customElements.define runs below.
function readGlobal(name) {
  try {
    return window[name];
  } catch {
    return null;
  }
}

const storage = {
  get session() {
    return readGlobal('sessionStorage');
  },
  get local() {
    return readGlobal('localStorage');
  },
  cookies: {
    set(name, value, maxAge) {
      document.cookie = `${name}=${value};path=/;max-age=${maxAge};SameSite=Lax`;
    },
  },
};

/**
 * Asks Shopify where the visitor is. Returns null on any failure — a timeout,
 * a non-200, or a shape we did not expect — so the caller shows nothing rather
 * than guessing a region.
 */
async function detectCountry() {
  const abort = new AbortController();
  const timer = setTimeout(() => abort.abort(), GEO_TIMEOUT_MS);
  try {
    const response = await fetch(GEO_ENDPOINT, { signal: abort.signal });
    if (!response.ok) return null;
    const data = await response.json();
    const code = data?.detected_values?.country?.handle;
    return typeof code === 'string' ? code : null;
  } catch {
    return null;
  } finally {
    clearTimeout(timer);
  }
}

/**
 * @typedef {Object} RegionBannerRefs
 * @property {HTMLElement} text
 * @property {HTMLAnchorElement} link
 *
 * @extends {Component<RegionBannerRefs>}
 */
class RegionBanner extends Component {
  connectedCallback() {
    super.connectedCallback();
    this.offer();
  }

  async offer() {
    if (shouldSuppress(storage)) return;

    const country = await detectCountry();
    if (!country) return;

    const result = resolveRegion(country, this.dataset.storeRegion);
    if (result.action !== 'offer') return;

    const { text, link } = this.refs;
    if (!text || !link) return;

    text.textContent = (this.dataset.prompt || '').replace(/__REGION__/g, result.label);
    link.textContent = (this.dataset.action || '').replace(/__REGION__/g, result.label);

    // UTM rather than a bare ref: Shopify's built-in analytics parses utm_* and
    // ignores arbitrary query parameters, so this is what makes arrivals countable.
    const url = new URL(result.url);
    url.searchParams.set('utm_source', `takanaka-${this.dataset.storeRegion}`);
    url.searchParams.set('utm_medium', 'region-banner');
    link.href = url.toString();

    link.addEventListener('click', () => recordChoice(storage, result.region), { once: true });

    this.hidden = false;
  }

  dismiss() {
    recordDismissal(storage);
    this.hidden = true;
  }
}

if (!customElements.get('region-banner')) {
  customElements.define('region-banner', RegionBanner);
}

// The footer switcher is rendered by a separate section and may not exist on
// every template — querySelectorAll returns nothing in that case, so the loop
// below is its own guard. Navigation is left alone; only the choice is recorded.
document.querySelectorAll('.region-switcher__link').forEach((el) => {
  el.addEventListener('click', () => recordChoice(storage, el.dataset.region));
});
